/**
 * Reconnect backoff (PRD §6: "silent auto-reconnect with backoff"). Pure so it
 * can be unit-tested; the WS client injects `Math.random` at the call site.
 */
export interface BackoffConfig {
  baseMs: number;
  maxMs: number;
  factor: number;
  /** Fraction of the ceiling that is randomized away, in [0, 1]. */
  jitter: number;
}

export const DEFAULT_BACKOFF: BackoffConfig = {
  baseMs: 500,
  maxMs: 15_000,
  factor: 2,
  jitter: 0.3,
};

/** Capped exponential ceiling for an attempt (0-based), no jitter. */
export function backoffCeiling(attempt: number, cfg: BackoffConfig = DEFAULT_BACKOFF): number {
  const a = Math.max(0, Math.floor(attempt));
  const raw = cfg.baseMs * Math.pow(cfg.factor, a);
  return Math.min(cfg.maxMs, raw);
}

/**
 * Full reconnect delay with subtractive jitter: a value in
 * `[ceiling*(1-jitter), ceiling]`. Subtractive jitter never exceeds the cap,
 * so a fleet of clients doesn't thunder past `maxMs`.
 */
export function reconnectDelay(
  attempt: number,
  cfg: BackoffConfig = DEFAULT_BACKOFF,
  rng: () => number = Math.random,
): number {
  const ceil = backoffCeiling(attempt, cfg);
  const jitterBand = ceil * cfg.jitter;
  return ceil - jitterBand + rng() * jitterBand;
}
